import { tinykeys, type KeybindingsMap } from 'tinykeys'

import { useUiStore, type ShortcutScope } from '@/stores/uiStore'

/**
 * Keyboard shortcuts with a scope stack.
 *
 * Scopes nest `global < editor < review < timeline` (§9.1); the stack lives
 * in `uiStore.scopes` and the **top** scope is the active one. A binding
 * registered for a scope fires only while that scope is on top, unless it is
 * `alwaysActive`, in which case it fires from any scope above its own too
 * (so `Ctrl+S` registered by the editor still works while the review bar is
 * on top of it).
 *
 * Input guard: while an `<input>`, `<textarea>`, `<select>` or a
 * contenteditable is focused, only `alwaysActive` bindings fire, so typing
 * "p" in a search box never switches tools. Bindings are matched on
 * `event.key` by tinykeys, which is what §9.3 asks for.
 */

export type Handler = (event: KeyboardEvent) => void

export interface ShortcutOptions {
  /** Fire even when an input is focused and when a higher scope is active. */
  alwaysActive?: boolean
  /** Call `preventDefault()` on match (default true). */
  preventDefault?: boolean
  /** Bind to this element instead of `window`. */
  target?: Window | HTMLElement
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

/** True when the event originates from an element that takes typed text. */
export function isTypingTarget(event: Event): boolean {
  const el = event.target
  if (!(el instanceof HTMLElement)) return false
  if (EDITABLE_TAGS.has(el.tagName)) {
    // Checkbox/radio/range/button inputs do not swallow letters.
    const type = (el as HTMLInputElement).type
    return !['checkbox', 'radio', 'range', 'button', 'submit'].includes(type)
  }
  // jsdom does not implement `isContentEditable`; fall back to the attribute.
  if (typeof el.isContentEditable === 'boolean') return el.isContentEditable
  return el.closest('[contenteditable]:not([contenteditable="false"])') !== null
}

function scopeIndex(scope: ShortcutScope): number {
  return useUiStore.getState().scopes.lastIndexOf(scope)
}

function isScopeActive(scope: ShortcutScope, alwaysActive: boolean): boolean {
  const scopes = useUiStore.getState().scopes
  const top = scopes[scopes.length - 1]
  if (top === scope) return true
  return alwaysActive && scopeIndex(scope) >= 0
}

/**
 * Register a map of tinykeys bindings for a scope. Returns the unsubscribe.
 *
 * ```ts
 * useEffect(() => registerShortcuts('global', { '$mod+k': openPalette }, { alwaysActive: true }), [])
 * ```
 */
export function registerShortcuts(scope: ShortcutScope, bindings: Record<string, Handler>, opts: ShortcutOptions = {}): () => void {
  const alwaysActive = opts.alwaysActive ?? false
  const preventDefault = opts.preventDefault ?? true
  const wrapped: KeybindingsMap = {}
  for (const [combo, handler] of Object.entries(bindings)) {
    wrapped[combo] = (event: KeyboardEvent) => {
      if (!isScopeActive(scope, alwaysActive)) return
      if (!alwaysActive && isTypingTarget(event)) return
      if (preventDefault) event.preventDefault()
      handler(event)
    }
  }
  // tinykeys' default filter drops events from inputs outright, which would
  // defeat `alwaysActive`; the typing guard above is the one we want.
  return tinykeys(opts.target ?? window, wrapped, { ignore: () => false })
}

/** Push a scope on mount, pop it on unmount. Use inside a `useEffect`. */
export function enterScope(scope: ShortcutScope): () => void {
  useUiStore.getState().pushScope(scope)
  return () => useUiStore.getState().popScope(scope)
}

// A listbox counts only while it is a popup that is open (Radix Select's content carries
// `data-state="open"`); an in-page list that merely uses the role — a list of rows in a panel —
// must not switch off Ctrl+Z / Ctrl+S for everything focused inside it. A listbox inside a dialog
// is still covered by the dialog.
const OVERLAY_SELECTOR = '[role="dialog"],[role="alertdialog"],[role="menu"],[role="listbox"][data-state="open"]'

/**
 * A modal dialog, menu or popup listbox (an open Radix Select) is open, or the event comes from
 * inside one. Editor shortcuts stay out of the way then: Ctrl+Z inside the "new kind" dialog must
 * not undo a shape on the picture behind it.
 */
export function isOverlayActive(event?: Event): boolean {
  const t = event?.target
  if (t instanceof Element && t.closest(OVERLAY_SELECTOR)) return true
  if (typeof document === 'undefined') return false
  return (
    document.querySelector(
      '[role="dialog"][data-state="open"],[role="alertdialog"][data-state="open"],[role="menu"][data-state="open"],[role="listbox"][data-state="open"]',
    ) !== null
  )
}

/**
 * The Latin letter a key press means for a Ctrl/⌘ shortcut, lower-cased, or null.
 *
 * `event.key` wins when it is a Latin letter, so AZERTY's Z (physical `KeyW`) still undoes and its
 * physical `KeyZ` (which types "w") does not. On a layout without Latin letters (Russian: the Z
 * key types "я") `event.code` names the physical key instead, which is where Ctrl+Z lives there.
 */
export function shortcutLetter(event: Pick<KeyboardEvent, 'key' | 'code'>): string | null {
  const key = event.key ?? ''
  if (/^[a-z]$/i.test(key)) return key.toLowerCase()
  if (key.length === 1 && /\p{L}/u.test(key)) {
    const m = /^Key([A-Z])$/.exec(event.code ?? '')
    return m ? m[1]!.toLowerCase() : null
  }
  // Dead keys / "Unidentified" with a modifier held: fall back to the physical key too.
  if (key === 'Unidentified' || key === 'Dead' || key === 'Process') {
    const m = /^Key([A-Z])$/.exec(event.code ?? '')
    return m ? m[1]!.toLowerCase() : null
  }
  return null
}

export type EditorCommand = 'undo' | 'redo' | 'save'

/**
 * Ctrl+Z → undo, Ctrl+Shift+Z / Ctrl+Y → redo, Ctrl+S → save; ⌘ instead of Ctrl on a Mac (either
 * modifier is accepted everywhere). Alt must be up: AltGr is Ctrl+Alt on Windows and types letters
 * such as "ż", which must never undo.
 */
export function editorCommandOf(event: Pick<KeyboardEvent, 'key' | 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): EditorCommand | null {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return null
  const letter = shortcutLetter(event)
  if (letter === 'z') return event.shiftKey ? 'redo' : 'undo'
  if (letter === 'y' && !event.shiftKey) return 'redo'
  if (letter === 's' && !event.shiftKey) return 'save'
  return null
}

/** Format a tinykeys combo for display: `$mod+k` → "Ctrl K" (or "⌘ K" on Mac). */
export function describeCombo(combo: string): string {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  return combo
    .split('+')
    .map((part) => {
      switch (part) {
        case '$mod':
          return mac ? '⌘' : 'Ctrl'
        case 'Shift':
          return mac ? '⇧' : 'Shift'
        case 'Alt':
          return mac ? '⌥' : 'Alt'
        case 'Control':
          return mac ? '⌃' : 'Ctrl'
        case 'Escape':
          return 'Esc'
        case ' ':
          return 'Space'
        default:
          return part.length === 1 ? part.toUpperCase() : part
      }
    })
    .join(' ')
}
